import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useErrorDisplay } from "../../contexts/ErrorDisplayContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { LAMBDA_BASE_URL } from "../../constants";

const ExcludeDomain: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const excludeDomainParam = searchParams.get("excludeDomain");
  const [search, setSearch] = useState("");
  const [newExclusion, setNewExclusion] = useState("");
  const [addError, setAddError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const { showError } = useErrorDisplay();

  const {
    data: listData,
    refetch: refetchList,
    isFetching: isListLoading,
  } = useQuery({
    queryKey: ["exclusions"],
    queryFn: async () => {
      const res = await axios.post<{
        excludedDomains: string[];
        excludedEmails: string[];
      }>(LAMBDA_BASE_URL, { listExclusions: true });
      return res.data;
    },
    enabled: true,
  });

  const excludeMutation = useMutation({
    mutationFn: (value: string) =>
      axios.post(LAMBDA_BASE_URL, { exclude: value }),
    onSuccess: () => {
      refetchList();
      setNewExclusion("");
      setAddError("");
      setShowAddForm(false);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err as Error)?.message ??
        "Failed to add exclusion.";
      setAddError(msg);
      showError(msg);
    },
  });

  const handleAddExclusion = () => {
    const value = newExclusion.trim();
    setAddError("");
    if (!value) {
      setAddError(
        "Enter a domain (e.g. example.com) or email (e.g. user@example.com).",
      );
      return;
    }
    excludeMutation.mutate(value);
  };

  const removeMutation = useMutation({
    mutationFn: (value: string) =>
      axios.post(LAMBDA_BASE_URL, { removeExclusion: value }),
    onSuccess: () => refetchList(),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err as Error)?.message ??
        "Failed to remove exclusion.";
      showError(msg);
    },
  });

  useEffect(() => {
    if (!excludeDomainParam) return;
    excludeMutation.mutate(decodeURIComponent(excludeDomainParam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeDomainParam]);

  const excludedDomains = useMemo(
    () => listData?.excludedDomains ?? [],
    [listData?.excludedDomains],
  );
  const excludedEmails = useMemo(
    () => listData?.excludedEmails ?? [],
    [listData?.excludedEmails],
  );
  const allExclusions = useMemo(
    () => [
      ...excludedDomains.map((d) => ({ value: d, isEmail: false })),
      ...excludedEmails.map((e) => ({ value: e, isEmail: true })),
    ],
    [excludedDomains, excludedEmails],
  );

  const searchLower = search.trim().toLowerCase();
  const filteredExclusions = useMemo(
    () =>
      searchLower
        ? allExclusions.filter((x) =>
            x.value.toLowerCase().includes(searchLower),
          )
        : allExclusions,
    [allExclusions, searchLower],
  );

  const justExcludedValue = excludeDomainParam
    ? decodeURIComponent(excludeDomainParam)
    : null;
  const showSuccessBanner =
    justExcludedValue && !excludeMutation.isPending && !excludeMutation.isError;

  return (
    <Box sx={{ maxWidth: 640, margin: "0 auto", px: 2, py: 3 }}>
      <Typography
        variant='h4'
        fontWeight={600}
        color='text.primary'
        gutterBottom>
        Exclusions
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
        Domains and emails listed here are excluded from syncing to Zoho{" "}
        <strong>only for your account</strong>. Other users are not affected.
      </Typography>

      {excludeMutation.isPending && justExcludedValue && (
        <Alert
          severity='info'
          icon={<CircularProgress size={20} />}
          sx={{ mb: 2 }}>
          Adding exclusion for <strong>{justExcludedValue}</strong>…
        </Alert>
      )}

      {showSuccessBanner && (
        <Alert
          severity='success'
          icon={
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "rgba(0,0,0,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <CheckCircleIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
          }
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: "#FF9800",
            color: "#000",
            alignItems: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            "& .MuiAlert-message": { color: "#000", flex: 1, fontWeight: 500 },
            "& .MuiAlert-action": { alignItems: "center" },
          }}
          action={
            <Button
              variant='contained'
              size='small'
              startIcon={<UndoIcon />}
              onClick={() =>
                removeMutation.mutate(justExcludedValue!, {
                  onSuccess: () => setSearchParams({}),
                })
              }
              disabled={removeMutation.isPending}
              sx={{
                bgcolor: "#374151",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { bgcolor: "#1f2937", boxShadow: "none" },
              }}>
              Revert
            </Button>
          }>
          <strong>{justExcludedValue}</strong> is now excluded.
        </Alert>
      )}

      <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: showAddForm ? 1.5 : 0,
          }}>
          <Typography variant='subtitle2' color='text.secondary'>
            Your excluded domains & emails
          </Typography>
          <Button
            variant='contained'
            color='primary'
            size='small'
            startIcon={<AddIcon />}
            onClick={() => setShowAddForm((v) => !v)}
            sx={{ textTransform: "none", px: 1.25, py: 0.5 }}>
            Add exclusion
          </Button>
        </Box>
        {showAddForm && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mb: 1 }}>
            <TextField
              size='small'
              placeholder='Domain (e.g. example.com) or email (e.g. user@example.com)'
              value={newExclusion}
              onChange={(e) => {
                setNewExclusion(e.target.value);
                setAddError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && handleAddExclusion()}
              error={!!addError}
              helperText={addError}
              sx={{ flex: 1 }}
              autoFocus
            />
            <Button
              variant='contained'
              onClick={handleAddExclusion}
              disabled={excludeMutation.isPending || !newExclusion.trim()}
              sx={{
                minWidth: 100,
                alignSelf: { xs: "stretch", sm: "flex-start" },
              }}>
              Exclude
            </Button>
          </Stack>
        )}
        <TextField
          fullWidth
          size='small'
          placeholder='Search exclusions…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' color='action' />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 1, mb: 2 }}
        />
        {isListLoading && allExclusions.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredExclusions.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {searchLower
              ? "No exclusions match your search."
              : "No exclusions yet."}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {filteredExclusions.map(({ value, isEmail }) => (
              <Chip
                key={value}
                label={value}
                size='medium'
                onDelete={() => removeMutation.mutate(value)}
                disabled={removeMutation.isPending}
                sx={{
                  borderRadius: 1.5,
                  "& .MuiChip-deleteIcon": { fontSize: 18 },
                }}
              />
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ExcludeDomain;
